import { Store } from '@ngrx/store';
import { LoadingGuard } from '@schir-int-client/ngrx-helpers';
import { verfahrenSingleSelector } from 'libs/verfahren-shared/src/lib/verfahren.selectors';
import { isNil } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import {
	setKontaktAssignmentAction,
	changeKontaktEditModeAction,
	createKontaktAction,
	getSearchKontakteOwner,
	loadKontakteByVerfahrenAction,
	searchKontakteAction,
	setSelectedKontaktAction,
	updateKontaktAction,
	updateVerfahrenKontaktFreiTextAction,
} from './adressverwaltung.actions';
import {
	Kontakt,
	KontaktInVerfahren,
	KontaktInVerfahrenListResource,
	KontaktKategorie,
	kontaktKategorienSortingForKontaktlist,
	KontaktListResource,
	KontaktResource,
	KontaktSearchMode,
	VerfahrenKontakt,
	VerfahrenKontaktResource,
} from './adressverwaltung.model';
import {
	editModeSelector,
	kontakteBySearchSelector,
	kontakteInVerfahrenSelector,
	selectedKontaktSelector,
} from './adressverwaltung.selectors';
import { AdressverwaltungRootState } from './adressverwaltung.state';
import { AdressverwaltungService } from './adressverwaltung.service';
import { VerfahrenLinkRel, VerfahrenListResource } from '@schir-int-client/verfahren-shared';
import { ElementRef, Injectable } from '@angular/core';
import { apiRootSelector } from '@schir-int-client/api-root';
import { getEmbeddedResource } from '@ngxp/rest';
import { KontaktListLinkRel } from './adressverwaltung.linkrel';

@Injectable({ providedIn: 'root' })
export class AdressverwaltungFacade {
	selectedKontakt: Observable<KontaktResource> = this.store.select(selectedKontaktSelector);
	editMode: Observable<boolean> = this.store.select(editModeSelector);

	private selectedKontaktActionRequestedSubject: Subject<void> = new Subject();
	selectedKontaktActionRequested: Observable<void> = this.selectedKontaktActionRequestedSubject.asObservable();

	kontakteInVerfahren: Observable<KontaktInVerfahrenListResource> = this.loadKontakteInVerfahren();

	// damit später angelegte Subscriptions den zuletzt geladenen Wert bekommen:
	private kontakteInKategorieSubjects: Record<KontaktKategorie, BehaviorSubject<KontaktResource[]>>;

	kontakteInKategorie: Record<KontaktKategorie, Observable<KontaktResource[]>>;

	private renderedKontaktSubject: Subject<ElementRef> = new Subject();
	private kontaktStoredSubject: Subject<Kontakt> = new Subject();

	renderedKontakt: Observable<ElementRef> = this.renderedKontaktSubject.asObservable();
	kontaktStored: Observable<Kontakt> = this.kontaktStoredSubject.asObservable();
	loadingKontakte = new LoadingGuard(this.store);
	markerForLoadingKontakte: string = 'initial';

	constructor(
		private store: Store<AdressverwaltungRootState>,
		private service: AdressverwaltungService
	) {
		this.kontakteInKategorieSubjects = {} as Record<KontaktKategorie, BehaviorSubject<KontaktResource[]>>;
		this.kontakteInKategorie = {} as Record<KontaktKategorie, Observable<KontaktResource[]>>;

		Object.values(KontaktKategorie).forEach(kategorie => {
			const behaviorSubject = new BehaviorSubject<KontaktResource[]>([]);
			this.kontakteInKategorieSubjects[kategorie] = behaviorSubject;
			this.kontakteInKategorie[kategorie] = behaviorSubject.asObservable();
		});

		this.kontakteInVerfahren.subscribe((kontakte: KontaktInVerfahrenListResource) => {
			Object.values(KontaktKategorie).forEach(kategorie => {
				const list = kontakte?._embedded.kontaktInVerfahrenResList
					.filter(kiv => kiv.inVerfahren.kategorien.includes(kategorie))
					.map(kiv => kiv.kontakt);
				this.kontakteInKategorieSubjects[kategorie].next(list);
			});
		});
	}

	private compareKontaktInVerfahren(a: KontaktInVerfahren, b: KontaktInVerfahren) {
		for (const kategorie of kontaktKategorienSortingForKontaktlist) {
			const aHas = a.inVerfahren.kategorien.includes(kategorie) ? 1 : 0;
			const bHas = b.inVerfahren.kategorien.includes(kategorie) ? 1 : 0;
			if (aHas !== bHas) return bHas - aHas;
		}
		return a.kontakt.title.localeCompare(b.kontakt.title);
	}
	private compareKontaktKategory(a: KontaktKategorie, b: KontaktKategorie) {
		const indexA = kontaktKategorienSortingForKontaktlist.indexOf(a);
		const indexB = kontaktKategorienSortingForKontaktlist.indexOf(b);
		return indexA - indexB;
	}

	rendered(kontaktDiv: ElementRef): void {
		this.renderedKontaktSubject.next(kontaktDiv);
	}

	private loadKontakteInVerfahren(): Observable<KontaktInVerfahrenListResource> {
		return combineLatest([
			this.store.select(verfahrenSingleSelector),
			this.store.select(kontakteInVerfahrenSelector),
		]).pipe(
			filter(([verfahren]) => !isNil(verfahren)),
			filter(
				([verfahren, kontakte]) =>
					!this.loadingKontakte.mustLoadFirst(
						kontakte,
						() => loadKontakteByVerfahrenAction({ verfahren }),
						verfahren._links[VerfahrenLinkRel.KONTAKTE].href + this.markerForLoadingKontakte
					)
			),
			map(([, kontakte]) => kontakte.resource),
			map(resource => {
				if (resource?._embedded?.kontaktInVerfahrenResList) {
					const deepCopy = JSON.parse(JSON.stringify(resource));
					deepCopy._embedded.kontaktInVerfahrenResList.sort(this.compareKontaktInVerfahren);
					deepCopy._embedded.kontaktInVerfahrenResList.forEach(kiv => {
						kiv.inVerfahren.kategorien.sort(this.compareKontaktKategory);
					});
					return deepCopy;
				}
				if (resource && !resource._embedded) {
					return { ...resource, _embedded: { kontaktInVerfahrenResList: [] } }; // avoid NPE if no kontakte
				}
				return resource;
			})
		);
	}

	// sucht in Verfahren mit Fallback auf alle Kontakte im Backend (unterstützt bestehende Adressaten)
	public getKontaktByUri(uri: string): Observable<KontaktResource> {
		return this.kontakteInVerfahren.pipe(
			filter(listRes => !isNil(listRes)),
			switchMap((listRes: KontaktInVerfahrenListResource) => {
				const found = listRes._embedded.kontaktInVerfahrenResList
					.map(kiv => kiv.kontakt)
					.find(kontakt => kontakt._links.self.href === uri);
				return found ? of(found) : this.service.get(uri);
			})
		);
	}

	private readonly defaultFilters = [
		KontaktSearchMode.NATUERLICH,
		KontaktSearchMode.JURISTISCH,
		KontaktSearchMode.BEHOERDEN,
		KontaktSearchMode.AKTIV,
	];
	private searchAction = searchKontakteAction({ condition: '', filter: this.defaultFilters.join(','), limit: 200 });
	private searchActionChanged = new BehaviorSubject<string>('initial');
	public kontakteBySearch: Observable<KontaktListResource> = this.searchKontakte();

	public setSearchParameters(condition: string, filter: string, limit: number) {
		this.searchAction = searchKontakteAction({ condition, filter, limit });
		this.searchActionChanged.next(getSearchKontakteOwner(condition, filter, limit));
	}

	private searchKontakte(): Observable<KontaktListResource> {
		const searchGuard = new LoadingGuard(this.store);
		return combineLatest([this.store.select(kontakteBySearchSelector), this.searchActionChanged]).pipe(
			filter(
				([kontakte]) =>
					this.searchAction &&
					!searchGuard.mustLoadOwnedFirst(
						kontakte,
						() => this.searchAction,
						getSearchKontakteOwner(
							this.searchAction.condition,
							this.searchAction.filter,
							this.searchAction.limit
						)
					)
			),
			map(([kontakte]) => kontakte.resource)
		);
	}

	normalize(input: string): string {
		return input
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLocaleLowerCase();
	}

	public createKontakt(kontakt: Kontakt): void {
		this.store.dispatch(createKontaktAction({ kontakt }));
		this.notifyKontaktStored(kontakt);
	}

	private notifyKontaktStored(kontakt: Kontakt) {
		this.store
			.select(selectedKontaktSelector)
			.pipe(
				filter(
					k => k.firmenName == kontakt.firmenName && k.vorname == kontakt.vorname && k.name == kontakt.name
				),
				take(1)
			)
			.subscribe(() => this.kontaktStoredSubject.next(kontakt));
	}

	public updateKontakt(kontakt: Kontakt): void {
		this.store.dispatch(updateKontaktAction({ kontakt }));
		this.notifyKontaktStored(kontakt);
	}

	public setKontaktAssignment(verfahrenKontakt: VerfahrenKontakt): void {
		this.store.dispatch(setKontaktAssignmentAction({ verfahrenKontakt }));
	}

	public setEditMode(editMode: boolean): void {
		this.store.dispatch(changeKontaktEditModeAction({ editMode }));
	}

	public setSelectedKontakt(kontakt: KontaktResource): void {
		this.store.dispatch(setSelectedKontaktAction({ kontakt }));
	}

	public updateVerfahrenKontaktFreiText(verfahrenKontakt: VerfahrenKontaktResource) {
		this.store.dispatch(updateVerfahrenKontaktFreiTextAction({ verfahrenKontakt }));
	}

	getAllVerfahrenForKontakt(selectedKontakt: KontaktResource): Observable<VerfahrenListResource> {
		return this.service.getAllVerfahrenForKontakt(selectedKontakt);
	}

	public existsOtherKontaktByName(
		selectedKontakt: KontaktResource,
		isJuristischePerson: boolean,
		firmenName: string,
		vorname: string,
		name: string
	): Observable<boolean> {
		return this.store.select(apiRootSelector).pipe(
			filter(root => !isNil(root)),
			switchMap(root => this.service.getKontakteByName(root, isJuristischePerson, firmenName, vorname, name)),
			map(k => {
				switch (k.page.size) {
					case 0:
						return false;
					case 1:
						return (
							getEmbeddedResource(k, KontaktListLinkRel.KONTAKT_LIST)[0]._links.self.href !==
							selectedKontakt?._links?.self.href
						);
					default:
						return true;
				}
			})
		);
	}

	public requestActionOnSelectedKontakt() {
		this.selectedKontaktActionRequestedSubject.next();
	}
}
